import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Sidebar from '../components/Sidebar';
import Footer from '../components/Footer';
import PayslipPDF from '../components/PayslipPDF';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Wallet, Settings, Play, FileText, Trash2, Plus, X, ChevronLeft, ChevronRight, Download, Eye, Lock, Unlock, Pencil
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

const Payroll = ({ user, onLogout }) => {
  const [activeTab, setActiveTab] = useState('process');
  const [structures, setStructures] = useState([]);
  const [payslips, setPayslips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);

  // Setup dialog
  const [setupDialog, setSetupDialog] = useState(false);
  const [setupEmail, setSetupEmail] = useState('');
  const [setupCTC, setSetupCTC] = useState('');
  const [setupEmpId, setSetupEmpId] = useState('');
  const [setupBank, setSetupBank] = useState('');
  const [setupDOJ, setSetupDOJ] = useState('');
  const [setupDisplayName, setSetupDisplayName] = useState('');
  const [setupDesignation, setSetupDesignation] = useState('');

  // Payslip preview
  const [previewPayslip, setPreviewPayslip] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Edit payslip dialog
  const [editDialog, setEditDialog] = useState(false);
  const [editPayslip, setEditPayslip] = useState(null);
  const [editSaving, setEditSaving] = useState(false);

  // Process payroll
  const now = new Date();
  const [selMonth, setSelMonth] = useState(now.getMonth() + 1);
  const [selYear, setSelYear] = useState(now.getFullYear());
  const [entries, setEntries] = useState([]);
  const [viewMonth, setViewMonth] = useState(now.getMonth() + 1);
  const [viewYear, setViewYear] = useState(now.getFullYear());

  // Lock state
  const [processLocked, setProcessLocked] = useState(false);
  const [viewLocked, setViewLocked] = useState(false);
  const [viewLockedBy, setViewLockedBy] = useState('');

  const token = localStorage.getItem('token');
  const authHeader = { headers: { Authorization: `Bearer ${token}` } };

  const fetchStructures = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/payroll/salary-structures`, authHeader);
      setStructures(res.data.structures || []);
    } catch (e) { toast.error('Failed to fetch salary data'); }
  }, []);

  const fetchPayslips = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/payroll/payslips?month=${viewMonth}&year=${viewYear}`, authHeader);
      setPayslips(res.data.payslips || []);
    } catch (e) { toast.error('Failed to fetch payslips'); }
    // Also fetch lock status for view month
    try {
      const lockRes = await axios.get(`${BACKEND_URL}/api/admin/payroll/lock-status?month=${viewMonth}&year=${viewYear}`, authHeader);
      setViewLocked(lockRes.data.locked);
      setViewLockedBy(lockRes.data.locked_by || '');
    } catch (e) { setViewLocked(false); }
  }, [viewMonth, viewYear]);

  // Fetch lock status for process month
  const fetchProcessLock = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/payroll/lock-status?month=${selMonth}&year=${selYear}`, authHeader);
      setProcessLocked(res.data.locked);
    } catch (e) { setProcessLocked(false); }
  }, [selMonth, selYear]);

  useEffect(() => { fetchStructures().then(() => setLoading(false)); }, [fetchStructures]);
  useEffect(() => { if (activeTab === 'view') fetchPayslips(); }, [activeTab, fetchPayslips]);
  useEffect(() => { if (activeTab === 'process') fetchProcessLock(); }, [activeTab, selMonth, selYear, fetchProcessLock]);

  const saveSalarySetup = async (e) => {
    e.preventDefault();
    if (!setupEmail || !setupCTC) { toast.error('Email and CTC required'); return; }
    try {
      await axios.post(`${BACKEND_URL}/api/admin/payroll/salary-setup`, {
        user_email: setupEmail, monthly_ctc: parseFloat(setupCTC),
        employee_id: setupEmpId, bank_account: setupBank, date_of_joining: setupDOJ,
        display_name: setupDisplayName, designation: setupDesignation
      }, authHeader);
      toast.success('Salary structure saved');
      setSetupDialog(false);
      setSetupEmail(''); setSetupCTC(''); setSetupEmpId(''); setSetupBank(''); setSetupDOJ(''); setSetupDisplayName(''); setSetupDesignation('');
      fetchStructures();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  // Calculate max paid days for a new joiner based on DOJ
  // Returns -1 if employee hasn't joined yet (should be excluded)
  const getJoinerPaidDays = (dojStr, month, year, totalDays) => {
    try {
      const parts = (dojStr || '').trim().split('/');
      if (parts.length !== 3) return totalDays;
      const [dDay, dMonth, dYear] = [parseInt(parts[0]), parseInt(parts[1]), parseInt(parts[2])];
      if (dYear === year && dMonth === month) {
        const daysInMonth = new Date(year, month, 0).getDate();
        const remaining = daysInMonth - dDay + 1;
        return Math.max(0, Math.round(totalDays * remaining / daysInMonth * 2) / 2); // round to 0.5
      }
      if (dYear > year || (dYear === year && dMonth > month)) return -1; // not yet joined
      return totalDays;
    } catch { return totalDays; }
  };

  const initProcessEntries = () => {
    const configured = structures.filter(s => s.monthly_ctc > 0);
    const validEntries = [];
    configured.forEach(s => {
      const totalDays = 26;
      const maxPaid = getJoinerPaidDays(s.date_of_joining, selMonth, selYear, totalDays);
      if (maxPaid === -1) return; // Employee hasn't joined yet — skip entirely
      validEntries.push({
        user_email: s.user_email,
        agent_name: s.display_name || s.agent_name,
        monthly_ctc: s.monthly_ctc,
        date_of_joining: s.date_of_joining || '',
        total_working_days: totalDays,
        paid_days: maxPaid,
        lop_days: Math.max(0, totalDays - maxPaid),
        incentive: 0,
        double_pay_days: 0,
        other_deductions: [],
        other_additions: [],
        is_new_joiner: maxPaid < totalDays && maxPaid > 0
      });
    });
    setEntries(validEntries);
  };

  useEffect(() => { if (activeTab === 'process') initProcessEntries(); }, [activeTab, structures, selMonth, selYear]);

  const updateEntry = (idx, field, value) => {
    setEntries(prev => {
      const updated = [...prev];
      updated[idx] = { ...updated[idx], [field]: value };
      if (field === 'paid_days' || field === 'total_working_days') {
        updated[idx].lop_days = Math.max(0, updated[idx].total_working_days - updated[idx].paid_days);
      }
      return updated;
    });
  };

  const addOtherDeduction = (idx) => {
    setEntries(prev => {
      const updated = [...prev];
      updated[idx].other_deductions = [...updated[idx].other_deductions, { name: '', amount: 0 }];
      return updated;
    });
  };

  const updateOtherDeduction = (entryIdx, dedIdx, field, value) => {
    setEntries(prev => {
      const updated = [...prev];
      updated[entryIdx].other_deductions = [...updated[entryIdx].other_deductions];
      updated[entryIdx].other_deductions[dedIdx] = { ...updated[entryIdx].other_deductions[dedIdx], [field]: value };
      return updated;
    });
  };

  const removeOtherDeduction = (entryIdx, dedIdx) => {
    setEntries(prev => {
      const updated = [...prev];
      updated[entryIdx].other_deductions = updated[entryIdx].other_deductions.filter((_, i) => i !== dedIdx);
      return updated;
    });
  };

  const addOtherAddition = (idx) => {
    setEntries(prev => {
      const updated = [...prev];
      updated[idx].other_additions = [...updated[idx].other_additions, { name: '', amount: 0 }];
      return updated;
    });
  };

  const updateOtherAddition = (entryIdx, addIdx, field, value) => {
    setEntries(prev => {
      const updated = [...prev];
      updated[entryIdx].other_additions = [...updated[entryIdx].other_additions];
      updated[entryIdx].other_additions[addIdx] = { ...updated[entryIdx].other_additions[addIdx], [field]: value };
      return updated;
    });
  };

  const removeOtherAddition = (entryIdx, addIdx) => {
    setEntries(prev => {
      const updated = [...prev];
      updated[entryIdx].other_additions = updated[entryIdx].other_additions.filter((_, i) => i !== addIdx);
      return updated;
    });
  };


  const processPayroll = async () => {
    if (entries.length === 0) { toast.error('No entries to process'); return; }
    if (!window.confirm(`Process payroll for ${MONTHS[selMonth - 1]} ${selYear} for ${entries.length} employees?`)) return;
    
    setProcessing(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/admin/payroll/process`, {
        month: selMonth, year: selYear,
        entries: entries.map(e => ({
          user_email: e.user_email,
          total_working_days: parseFloat(e.total_working_days),
          paid_days: parseFloat(e.paid_days),
          lop_days: parseFloat(e.lop_days),
          incentive: parseFloat(e.incentive) || 0,
          double_pay_days: parseFloat(e.double_pay_days) || 0,
          other_deductions: e.other_deductions.filter(d => d.name && d.amount > 0).map(d => ({ name: d.name, amount: parseFloat(d.amount) })),
          other_additions: e.other_additions.filter(a => a.name && a.amount > 0).map(a => ({ name: a.name, amount: parseFloat(a.amount) }))
        }))
      }, authHeader);
      toast.success(res.data.message);
      if (res.data.errors?.length) toast.error(`Errors: ${res.data.errors.join(', ')}`);
      setActiveTab('view');
      setViewMonth(selMonth);
      setViewYear(selYear);
    } catch (e) { toast.error('Failed to process payroll'); }
    finally { setProcessing(false); }
  };

  const deletePayslip = async (id) => {
    if (viewLocked) { toast.error('This month is locked. Unlock to delete.'); return; }
    if (!window.confirm('Delete this payslip?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/payroll/payslip/${id}`, authHeader);
      toast.success('Deleted');
      fetchPayslips();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const toggleLock = async () => {
    const action = viewLocked ? 'unlock' : 'lock';
    const confirmMsg = viewLocked
      ? `Unlock payroll for ${MONTHS[viewMonth - 1]} ${viewYear}? This will allow modifications.`
      : `Lock payroll for ${MONTHS[viewMonth - 1]} ${viewYear}? No changes can be made until unlocked.`;
    if (!window.confirm(confirmMsg)) return;
    try {
      await axios.post(`${BACKEND_URL}/api/admin/payroll/${action}`, { month: viewMonth, year: viewYear }, authHeader);
      toast.success(viewLocked ? 'Payroll unlocked' : 'Payroll locked');
      fetchPayslips();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const viewPayslipDetail = async (payslipId) => {
    setDetailLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/payroll/payslip/${payslipId}`, authHeader);
      setPreviewPayslip(res.data);
    } catch (e) { toast.error('Failed to load payslip'); }
    finally { setDetailLoading(false); }
  };

  const openEditPayslip = async (payslipId) => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/payroll/payslip/${payslipId}`, authHeader);
      setEditPayslip(res.data);
      setEditDialog(true);
    } catch (e) { toast.error('Failed to load payslip'); }
  };

  const updateEditField = (field, value) => {
    setEditPayslip(prev => {
      const updated = { ...prev, [field]: value };
      if (field === 'paid_days' || field === 'total_working_days') {
        updated.lop_days = Math.max(0, (updated.total_working_days || 0) - (updated.paid_days || 0));
      }
      return updated;
    });
  };

  const saveEditPayslip = async () => {
    if (!editPayslip) return;
    setEditSaving(true);
    try {
      await axios.put(`${BACKEND_URL}/api/admin/payroll/payslip/${editPayslip.payslip_id}`, {
        total_working_days: parseFloat(editPayslip.total_working_days),
        paid_days: parseFloat(editPayslip.paid_days),
        lop_days: parseFloat(editPayslip.lop_days),
        incentive: parseFloat(editPayslip.incentive) || 0,
        double_pay_days: parseFloat(editPayslip.double_pay_days) || 0,
        other_deductions: (editPayslip.other_deductions || []).filter(d => d.name && d.amount > 0),
        other_additions: (editPayslip.other_additions || []).filter(a => a.name && a.amount > 0),
      }, authHeader);
      toast.success('Payslip updated');
      setEditDialog(false);
      setEditPayslip(null);
      fetchPayslips();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update'); }
    finally { setEditSaving(false); }
  };


  const editSetup = (s) => {
    setSetupEmail(s.user_email);
    setSetupCTC(s.monthly_ctc?.toString() || '');
    setSetupEmpId(s.employee_id || '');
    setSetupBank(s.bank_account || '');
    setSetupDOJ(s.date_of_joining || '');
    setSetupDisplayName(s.display_name || '');
    setSetupDesignation(s.designation || '');
    setSetupDialog(true);
  };

  const openNewSetup = () => {
    setSetupEmail('');
    setSetupCTC('');
    setSetupEmpId('');
    setSetupBank('');
    setSetupDOJ('');
    setSetupDisplayName('');
    setSetupDesignation('');
    setSetupDialog(true);
  };

  const formatCurrency = (n) => `₹${(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  if (loading) return (
    <div className="flex min-h-screen bg-slate-900">
      <Sidebar user={user} onLogout={onLogout} />
      <div className="flex-1 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    </div>
  );

  return (
    <div className="flex min-h-screen bg-slate-900" data-testid="payroll-page">
      <Sidebar user={user} onLogout={onLogout} />
      <div className="flex-1 flex flex-col">
        <div className="flex-1 p-6 overflow-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <Wallet className="w-8 h-8 text-emerald-400" />
              <div>
                <h1 className="text-2xl font-bold text-white">Payroll Management</h1>
                <p className="text-slate-400 text-sm">Manage salaries, process payroll & generate payslips</p>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 mb-6">
            {[
              { key: 'process', label: 'Process Payroll', icon: Play },
              { key: 'setup', label: 'Salary Setup', icon: Settings },
              { key: 'view', label: 'View Payslips', icon: FileText },
            ].map(tab => (
              <button key={tab.key} onClick={() => setActiveTab(tab.key)}
                data-testid={`payroll-tab-${tab.key}`}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  activeTab === tab.key ? 'bg-emerald-500 text-white' : 'bg-slate-800 text-slate-400 hover:text-white hover:bg-slate-700'
                }`}>
                <tab.icon className="w-4 h-4" />{tab.label}
              </button>
            ))}
          </div>

          {/* Salary Setup Tab */}
          {activeTab === 'setup' && (
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-white">Employee Salary Structures</CardTitle>
                <Button size="sm" onClick={openNewSetup} className="bg-emerald-500 hover:bg-emerald-600" data-testid="add-salary-setup-btn">
                  <Plus className="w-4 h-4 mr-1" />Add Setup
                </Button>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow className="border-slate-700">
                      <TableHead className="text-slate-400">Employee</TableHead>
                      <TableHead className="text-slate-400">Display Name</TableHead>
                      <TableHead className="text-slate-400">Designation</TableHead>
                      <TableHead className="text-slate-400">Emp ID</TableHead>
                      <TableHead className="text-slate-400">Monthly CTC</TableHead>
                      <TableHead className="text-slate-400">DOJ</TableHead>
                      <TableHead className="text-slate-400 w-20">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {structures.map(s => (
                      <TableRow key={s.user_email} className="border-slate-700" data-testid={`salary-row-${s.user_email}`}>
                        <TableCell className="text-white font-medium">{s.agent_name || s.user_email}</TableCell>
                        <TableCell className="text-amber-400 font-medium">{s.display_name || <span className="text-slate-600 italic">—</span>}</TableCell>
                        <TableCell className="text-slate-300">{s.designation || <span className="text-slate-600 italic">—</span>}</TableCell>
                        <TableCell className="text-slate-400 font-mono">{s.employee_id || '—'}</TableCell>
                        <TableCell className={s.monthly_ctc > 0 ? 'text-emerald-400 font-semibold' : 'text-red-400'}>{s.monthly_ctc > 0 ? formatCurrency(s.monthly_ctc) : 'Not Set'}</TableCell>
                        <TableCell className="text-slate-300">{s.date_of_joining || '—'}</TableCell>
                        <TableCell>
                          <Button size="sm" variant="outline" onClick={() => editSetup(s)}
                            className="text-xs" data-testid={`edit-setup-${s.user_email}`}>
                            {s.monthly_ctc > 0 ? 'Edit' : 'Setup'}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}

          {/* Process Payroll Tab */}
          {activeTab === 'process' && (
            <div className="space-y-4">
              <Card className="bg-slate-800 border-slate-700">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div>
                        <Label className="text-slate-300 text-xs">Month</Label>
                        <Select value={String(selMonth)} onValueChange={v => setSelMonth(parseInt(v))}>
                          <SelectTrigger className="w-40 bg-slate-900 border-slate-600 text-white" data-testid="select-month"><SelectValue /></SelectTrigger>
                          <SelectContent>{MONTHS.map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-slate-300 text-xs">Year</Label>
                        <Input type="number" value={selYear} onChange={e => setSelYear(parseInt(e.target.value))}
                          className="w-24 bg-slate-900 border-slate-600 text-white" data-testid="input-year" />
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {processLocked && (
                        <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm" data-testid="process-lock-warning">
                          <Lock className="w-4 h-4" /> Locked — switch to View Payslips to unlock
                        </div>
                      )}
                      <Button onClick={processPayroll} disabled={processing || entries.length === 0 || processLocked}
                        className={processLocked ? 'bg-slate-600 cursor-not-allowed' : 'bg-emerald-500 hover:bg-emerald-600'} data-testid="process-payroll-btn">
                        <Play className="w-4 h-4 mr-2" />{processing ? 'Processing...' : `Process ${entries.length} Payslips`}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {entries.length === 0 ? (
                <Card className="bg-slate-800 border-slate-700">
                  <CardContent className="py-12 text-center">
                    <p className="text-slate-400">No employees with salary configured. Go to Salary Setup first.</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-3">
                  {entries.map((entry, idx) => (
                    <Card key={entry.user_email} className="bg-slate-800 border-slate-700" data-testid={`process-entry-${entry.user_email}`}>
                      <CardContent className="p-4">
                        <div className="flex items-start gap-6">
                          <div className="flex-shrink-0 w-40">
                            <p className="text-white font-semibold">{entry.agent_name}</p>
                            <p className="text-slate-500 text-xs">CTC: {formatCurrency(entry.monthly_ctc)}</p>
                            {entry.is_new_joiner && (
                              <p className="text-amber-400 text-xs mt-1">New Joiner — DOJ: {entry.date_of_joining}</p>
                            )}
                          </div>
                          <div className="flex-1 grid grid-cols-6 gap-3">
                            <div>
                              <Label className="text-slate-400 text-xs">Working Days</Label>
                              <Input type="number" step="0.5" value={entry.total_working_days}
                                onChange={e => updateEntry(idx, 'total_working_days', parseFloat(e.target.value) || 0)}
                                className="bg-slate-900 border-slate-600 text-white h-8 text-sm" />
                            </div>
                            <div>
                              <Label className="text-slate-400 text-xs">Paid Days</Label>
                              <Input type="number" step="0.5" value={entry.paid_days}
                                onChange={e => updateEntry(idx, 'paid_days', parseFloat(e.target.value) || 0)}
                                className="bg-slate-900 border-slate-600 text-white h-8 text-sm" />
                            </div>
                            <div>
                              <Label className="text-slate-400 text-xs">LOP Days</Label>
                              <Input type="number" value={entry.lop_days} readOnly
                                className="bg-slate-900/50 border-slate-700 text-slate-400 h-8 text-sm" />
                            </div>
                            <div>
                              <Label className="text-slate-400 text-xs">Double Pay</Label>
                              <Input type="number" step="0.5" value={entry.double_pay_days}
                                onChange={e => updateEntry(idx, 'double_pay_days', parseFloat(e.target.value) || 0)}
                                className="bg-slate-900 border-slate-600 text-amber-400 h-8 text-sm" placeholder="0" />
                            </div>
                            <div>
                              <Label className="text-slate-400 text-xs">Incentive (₹)</Label>
                              <Input type="number" value={entry.incentive}
                                onChange={e => updateEntry(idx, 'incentive', parseFloat(e.target.value) || 0)}
                                className="bg-slate-900 border-slate-600 text-white h-8 text-sm" />
                            </div>
                            <div>
                              <Label className="text-slate-400 text-xs">Est. Net Pay</Label>
                              <p className="text-emerald-400 font-bold text-sm mt-1">
                                {formatCurrency(
                                  (entry.monthly_ctc * (entry.paid_days / (entry.total_working_days || 1)))
                                  + (entry.incentive || 0)
                                  + ((entry.double_pay_days || 0) * (entry.monthly_ctc / (entry.total_working_days || 1)))
                                  + (entry.other_additions || []).reduce((s, a) => s + (parseFloat(a.amount) || 0), 0)
                                  - 200
                                  - entry.other_deductions.reduce((s, d) => s + (parseFloat(d.amount) || 0), 0)
                                )}
                              </p>
                            </div>
                          </div>
                          <div className="flex-shrink-0 flex flex-col gap-1">
                            <Button size="sm" variant="ghost" onClick={() => addOtherAddition(idx)}
                              className="text-xs text-emerald-400 hover:text-emerald-300">
                              <Plus className="w-3 h-3 mr-1" />Addition
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => addOtherDeduction(idx)}
                              className="text-xs text-amber-400 hover:text-amber-300">
                              <Plus className="w-3 h-3 mr-1" />Deduction
                            </Button>
                          </div>
                        </div>
                        {entry.other_deductions.length > 0 && (
                          <div className="mt-3 pl-40 space-y-2">
                            <span className="text-xs text-red-400 font-medium">Deductions:</span>
                            {entry.other_deductions.map((ded, di) => (
                              <div key={di} className="flex items-center gap-2">
                                <Input value={ded.name} onChange={e => updateOtherDeduction(idx, di, 'name', e.target.value)}
                                  placeholder="Deduction name" className="bg-slate-900 border-slate-600 text-white h-8 text-sm w-48" />
                                <Input type="number" value={ded.amount} onChange={e => updateOtherDeduction(idx, di, 'amount', e.target.value)}
                                  placeholder="Amount" className="bg-slate-900 border-slate-600 text-white h-8 text-sm w-28" />
                                <button onClick={() => removeOtherDeduction(idx, di)} className="text-red-400 hover:text-red-300">
                                  <X className="w-4 h-4" />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                        {(entry.other_additions || []).length > 0 && (
                          <div className="mt-3 pl-40 space-y-2">
                            <span className="text-xs text-emerald-400 font-medium">Additions:</span>
                            {entry.other_additions.map((add, ai) => (
                              <div key={ai} className="flex items-center gap-2">
                                <Input value={add.name} onChange={e => updateOtherAddition(idx, ai, 'name', e.target.value)}
                                  placeholder="Addition name" className="bg-slate-900 border-slate-600 text-white h-8 text-sm w-48" />
                                <Input type="number" value={add.amount} onChange={e => updateOtherAddition(idx, ai, 'amount', e.target.value)}
                                  placeholder="Amount" className="bg-slate-900 border-slate-600 text-emerald-400 h-8 text-sm w-28" />
                                <button onClick={() => removeOtherAddition(idx, ai)} className="text-red-400 hover:text-red-300">
                                  <X className="w-4 h-4" />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* View Payslips Tab */}
          {activeTab === 'view' && (
            <div className="space-y-4">
              <Card className="bg-slate-800 border-slate-700">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <button onClick={() => { const m = viewMonth === 1 ? 12 : viewMonth - 1; const y = viewMonth === 1 ? viewYear - 1 : viewYear; setViewMonth(m); setViewYear(y); }}
                        className="text-slate-400 hover:text-white p-1" data-testid="prev-month-btn"><ChevronLeft className="w-5 h-5" /></button>
                      <span className="text-white font-semibold text-lg" data-testid="payslips-month-label">{MONTHS[viewMonth - 1]} {viewYear}</span>
                      <button onClick={() => { const m = viewMonth === 12 ? 1 : viewMonth + 1; const y = viewMonth === 12 ? viewYear + 1 : viewYear; setViewMonth(m); setViewYear(y); }}
                        className="text-slate-400 hover:text-white p-1" data-testid="next-month-btn"><ChevronRight className="w-5 h-5" /></button>
                    </div>
                    <div className="flex items-center gap-3">
                      {viewLocked && (
                        <div className="flex items-center gap-1.5 px-2.5 py-1 rounded bg-red-500/10 border border-red-500/30 text-red-400 text-xs font-medium">
                          <Lock className="w-3.5 h-3.5" /> Locked
                        </div>
                      )}
                      <p className="text-slate-400 text-sm">{payslips.length} payslips</p>
                      {payslips.length > 0 && (
                        <button onClick={toggleLock} data-testid="lock-toggle-btn"
                          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                            viewLocked
                              ? 'bg-amber-500/10 border border-amber-500/30 text-amber-400 hover:bg-amber-500/20'
                              : 'bg-slate-700 border border-slate-600 text-slate-300 hover:bg-slate-600'
                          }`}>
                          {viewLocked ? <><Unlock className="w-3.5 h-3.5" /> Unlock</> : <><Lock className="w-3.5 h-3.5" /> Lock Month</>}
                        </button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {payslips.length === 0 ? (
                <Card className="bg-slate-800 border-slate-700">
                  <CardContent className="py-12 text-center">
                    <FileText className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                    <p className="text-slate-400">No payslips for {MONTHS[viewMonth - 1]} {viewYear}</p>
                  </CardContent>
                </Card>
              ) : (
                <Card className="bg-slate-800 border-slate-700">
                  <CardContent className="p-0">
                    <Table>
                      <TableHeader>
                        <TableRow className="border-slate-700">
                          <TableHead className="text-slate-400">Employee</TableHead>
                          <TableHead className="text-slate-400 text-center">Paid Days</TableHead>
                          <TableHead className="text-slate-400 text-right">Gross</TableHead>
                          <TableHead className="text-slate-400 text-right">Incentive</TableHead>
                          <TableHead className="text-slate-400 text-right">Deductions</TableHead>
                          <TableHead className="text-slate-400 text-right">Net Pay</TableHead>
                          <TableHead className="text-slate-400 w-28 text-center">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {payslips.map(p => (
                          <TableRow key={p.payslip_id} className="border-slate-700" data-testid={`admin-payslip-${p.payslip_id}`}>
                            <TableCell className="text-white font-medium">{p.agent_name}</TableCell>
                            <TableCell className="text-slate-300 text-center">{p.paid_days}/{p.total_working_days}</TableCell>
                            <TableCell className="text-slate-300 text-right">{formatCurrency(p.gross_earnings)}</TableCell>
                            <TableCell className="text-amber-400 text-right">{p.incentive > 0 ? formatCurrency(p.incentive) : '—'}</TableCell>
                            <TableCell className="text-red-400 text-right">{formatCurrency(p.total_deductions)}</TableCell>
                            <TableCell className="text-emerald-400 font-bold text-right">{formatCurrency(p.net_pay)}</TableCell>
                            <TableCell className="text-center">
                              <div className="flex items-center justify-center gap-1">
                                <button onClick={() => viewPayslipDetail(p.payslip_id)}
                                  className="text-slate-400 hover:text-emerald-400 p-1" title="View/Download"
                                  data-testid={`view-payslip-btn-${p.payslip_id}`}>
                                  <Eye className="w-4 h-4" />
                                </button>
                                <button onClick={() => openEditPayslip(p.payslip_id)}
                                  className={`p-1 ${viewLocked ? 'text-slate-700 cursor-not-allowed' : 'text-slate-400 hover:text-blue-400'}`}
                                  title={viewLocked ? 'Month is locked' : 'Edit'}
                                  disabled={viewLocked}
                                  data-testid={`edit-payslip-btn-${p.payslip_id}`}>
                                  <Pencil className="w-4 h-4" />
                                </button>
                                <button onClick={() => deletePayslip(p.payslip_id)}
                                  className={`p-1 ${viewLocked ? 'text-slate-700 cursor-not-allowed' : 'text-slate-400 hover:text-red-400'}`}
                                  title={viewLocked ? 'Month is locked' : 'Delete'}
                                  disabled={viewLocked}
                                  data-testid={`delete-payslip-btn-${p.payslip_id}`}>
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </div>
        <Footer />
      </div>

      {/* Salary Setup Dialog */}
      <Dialog open={setupDialog} onOpenChange={setSetupDialog}>
        <DialogContent className="bg-slate-800 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2"><Settings className="w-5 h-5 text-emerald-400" />Salary Setup</DialogTitle>
          </DialogHeader>
          <form onSubmit={saveSalarySetup} className="space-y-3" data-testid="salary-setup-form">
            <div>
              <Label className="text-slate-300">Employee</Label>
              {setupEmail ? (
                <p className="text-white font-medium">{structures.find(s => s.user_email === setupEmail)?.agent_name || setupEmail}</p>
              ) : (
                <Select value={setupEmail} onValueChange={setSetupEmail}>
                  <SelectTrigger className="bg-slate-900 border-slate-600 text-white" data-testid="select-employee"><SelectValue placeholder="Select employee" /></SelectTrigger>
                  <SelectContent>{structures.filter(s => !s.monthly_ctc).map(s => <SelectItem key={s.user_email} value={s.user_email}>{s.agent_name || s.user_email}</SelectItem>)}</SelectContent>
                </Select>
              )}
            </div>
            <div>
              <Label className="text-slate-300">Display Name (for Salary Slip)</Label>
              <Input value={setupDisplayName} onChange={e => setSetupDisplayName(e.target.value)}
                placeholder="Name as it appears on payslip" className="bg-slate-900 border-slate-600 text-white" data-testid="input-display-name" />
              <p className="text-slate-500 text-xs mt-1">If blank, system username will be used</p>
            </div>
            <div>
              <Label className="text-slate-300">Designation (for Salary Slip)</Label>
              <Input value={setupDesignation} onChange={e => setSetupDesignation(e.target.value)}
                placeholder="e.g., Senior Sales Executive" className="bg-slate-900 border-slate-600 text-white" data-testid="input-designation" />
            </div>
            <div>
              <Label className="text-slate-300">Monthly CTC (₹) *</Label>
              <Input type="number" value={setupCTC} onChange={e => setSetupCTC(e.target.value)}
                placeholder="e.g., 45000" className="bg-slate-900 border-slate-600 text-white" data-testid="input-ctc" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-slate-300">Employee ID</Label>
                <Input value={setupEmpId} onChange={e => setSetupEmpId(e.target.value)}
                  placeholder="e.g., 003" className="bg-slate-900 border-slate-600 text-white" data-testid="input-emp-id" />
              </div>
              <div>
                <Label className="text-slate-300">Date of Joining</Label>
                <Input value={setupDOJ} onChange={e => setSetupDOJ(e.target.value)}
                  placeholder="DD/MM/YYYY" className="bg-slate-900 border-slate-600 text-white" data-testid="input-doj" />
              </div>
            </div>
            <div>
              <Label className="text-slate-300">Bank Account No</Label>
              <Input value={setupBank} onChange={e => setSetupBank(e.target.value)}
                placeholder="Account number" className="bg-slate-900 border-slate-600 text-white" data-testid="input-bank" />
            </div>
            {setupCTC && parseFloat(setupCTC) > 0 && (
              <div className="bg-slate-900 rounded-lg p-3 text-sm space-y-1" data-testid="salary-breakdown-preview">
                <p className="text-slate-400">Salary Breakdown:</p>
                <p className="text-white">Basic: {formatCurrency(parseFloat(setupCTC) * 0.5)}</p>
                <p className="text-white">HRA: {formatCurrency(parseFloat(setupCTC) * 0.2)}</p>
                <p className="text-white">Conveyance: {formatCurrency(parseFloat(setupCTC) * 0.175)}</p>
                <p className="text-white">Medical: {formatCurrency(parseFloat(setupCTC) * 0.125)}</p>
              </div>
            )}
            <div className="flex gap-2">
              <Button type="button" variant="outline" className="flex-1" onClick={() => setSetupDialog(false)}>Cancel</Button>
              <Button type="submit" className="flex-1 bg-emerald-500 hover:bg-emerald-600" data-testid="save-salary-btn">Save</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Payslip Preview/Download Dialog */}
      <Dialog open={!!previewPayslip} onOpenChange={() => setPreviewPayslip(null)}>
        <DialogContent className="bg-slate-800 border-slate-700 max-w-4xl max-h-[90vh] overflow-auto">
          <DialogHeader><DialogTitle className="sr-only">Payslip Preview</DialogTitle></DialogHeader>
          {detailLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : previewPayslip ? (
            <PayslipPDF payslip={previewPayslip} onClose={() => setPreviewPayslip(null)} />
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Edit Payslip Dialog */}
      <Dialog open={editDialog} onOpenChange={() => { setEditDialog(false); setEditPayslip(null); }}>
        <DialogContent className="bg-slate-800 border-slate-700 max-w-2xl max-h-[90vh] overflow-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2"><Pencil className="w-5 h-5 text-blue-400" />Edit Payslip — {editPayslip?.agent_name}</DialogTitle>
          </DialogHeader>
          {editPayslip && (
            <div className="space-y-4" data-testid="edit-payslip-form">
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-slate-300 text-xs">Working Days</Label>
                  <Input type="number" step="0.5" value={editPayslip.total_working_days}
                    onChange={e => updateEditField('total_working_days', parseFloat(e.target.value) || 0)}
                    className="bg-slate-900 border-slate-600 text-white" />
                </div>
                <div>
                  <Label className="text-slate-300 text-xs">Paid Days</Label>
                  <Input type="number" step="0.5" value={editPayslip.paid_days}
                    onChange={e => updateEditField('paid_days', parseFloat(e.target.value) || 0)}
                    className="bg-slate-900 border-slate-600 text-white" />
                </div>
                <div>
                  <Label className="text-slate-300 text-xs">LOP Days</Label>
                  <Input type="number" value={editPayslip.lop_days} readOnly
                    className="bg-slate-900/50 border-slate-700 text-slate-400" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-slate-300 text-xs">Double Pay Days</Label>
                  <Input type="number" step="0.5" value={editPayslip.double_pay_days || 0}
                    onChange={e => updateEditField('double_pay_days', parseFloat(e.target.value) || 0)}
                    className="bg-slate-900 border-slate-600 text-amber-400" />
                </div>
                <div>
                  <Label className="text-slate-300 text-xs">Incentive (₹)</Label>
                  <Input type="number" value={editPayslip.incentive || 0}
                    onChange={e => updateEditField('incentive', parseFloat(e.target.value) || 0)}
                    className="bg-slate-900 border-slate-600 text-white" />
                </div>
              </div>

              {/* Other Additions */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-emerald-400 text-xs font-semibold">Other Additions</span>
                  <Button size="sm" variant="ghost" onClick={() => updateEditField('other_additions', [...(editPayslip.other_additions || []), { name: '', amount: 0 }])}
                    className="text-xs text-emerald-400 h-6"><Plus className="w-3 h-3 mr-1" />Add</Button>
                </div>
                {(editPayslip.other_additions || []).map((a, i) => (
                  <div key={i} className="flex items-center gap-2 mb-2">
                    <Input value={a.name} onChange={e => {
                      const arr = [...(editPayslip.other_additions || [])]; arr[i] = { ...arr[i], name: e.target.value };
                      updateEditField('other_additions', arr);
                    }} placeholder="Name" className="bg-slate-900 border-slate-600 text-white h-8 text-sm flex-1" />
                    <Input type="number" value={a.amount} onChange={e => {
                      const arr = [...(editPayslip.other_additions || [])]; arr[i] = { ...arr[i], amount: parseFloat(e.target.value) || 0 };
                      updateEditField('other_additions', arr);
                    }} placeholder="₹" className="bg-slate-900 border-slate-600 text-emerald-400 h-8 text-sm w-28" />
                    <button onClick={() => updateEditField('other_additions', (editPayslip.other_additions || []).filter((_, j) => j !== i))}
                      className="text-red-400 hover:text-red-300"><X className="w-4 h-4" /></button>
                  </div>
                ))}
              </div>

              {/* Other Deductions */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-red-400 text-xs font-semibold">Other Deductions</span>
                  <Button size="sm" variant="ghost" onClick={() => updateEditField('other_deductions', [...(editPayslip.other_deductions || []), { name: '', amount: 0 }])}
                    className="text-xs text-red-400 h-6"><Plus className="w-3 h-3 mr-1" />Add</Button>
                </div>
                {(editPayslip.other_deductions || []).map((d, i) => (
                  <div key={i} className="flex items-center gap-2 mb-2">
                    <Input value={d.name} onChange={e => {
                      const arr = [...(editPayslip.other_deductions || [])]; arr[i] = { ...arr[i], name: e.target.value };
                      updateEditField('other_deductions', arr);
                    }} placeholder="Name" className="bg-slate-900 border-slate-600 text-white h-8 text-sm flex-1" />
                    <Input type="number" value={d.amount} onChange={e => {
                      const arr = [...(editPayslip.other_deductions || [])]; arr[i] = { ...arr[i], amount: parseFloat(e.target.value) || 0 };
                      updateEditField('other_deductions', arr);
                    }} placeholder="₹" className="bg-slate-900 border-slate-600 text-red-400 h-8 text-sm w-28" />
                    <button onClick={() => updateEditField('other_deductions', (editPayslip.other_deductions || []).filter((_, j) => j !== i))}
                      className="text-red-400 hover:text-red-300"><X className="w-4 h-4" /></button>
                  </div>
                ))}
              </div>

              <div className="flex gap-2 pt-2">
                <Button variant="outline" className="flex-1" onClick={() => { setEditDialog(false); setEditPayslip(null); }}>Cancel</Button>
                <Button className="flex-1 bg-blue-500 hover:bg-blue-600" onClick={saveEditPayslip} disabled={editSaving}
                  data-testid="save-edit-payslip-btn">
                  {editSaving ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Payroll;
